import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { FormProvider, useFormState, type FieldValues } from 'react-hook-form';

import { FormStateProvider, type FormScreenModel } from '@/shared/form';
import { useIsMobile } from '@/shared/mobile';
import { PageHeader, resolvePatternText } from '@/shared/patterns';
import { Alert } from '@/shared/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { ChoiceCommitProvider } from '@/shared/ui/choice-card';
import { cn } from '@/shared/ui/cn';

import type { FlowLeadingStep, FlowStep } from './define-flow';
import { FlowFrame, FlowMobileHeader, FlowStepHead } from './flow-frame';
import { FlowProgress } from './flow-progress';
import { useAutoAdvance, useFlowTrail } from './flow-trail';
import { FLOW_STEP_TRANSITION, withFlowTransition } from './flow-transition';
import { orphanFieldErrors, type FlowModel } from './use-flow';

/** A confirmação da revisão (Car Equity: "Confirmar envio para análise", SummaryContainer.tsx:575-595, :640-735). */
export interface FlowConfirm {
  readonly title: string;
  readonly description: string;
  /** Verbo da ação (U20.6): "Confirmar e enviar". */
  readonly submitLabel: string;
  readonly cancelLabel?: string;
  /** "Editar dados": fecha e volta ao passo `editStep`. */
  readonly editLabel?: string;
  readonly editStep?: string;
  /** Resumo curto dentro do diálogo (valores, veículo…). */
  readonly content?: ReactNode;
}

export interface FlowScreenProps<V extends FieldValues, R> {
  readonly screen: FormScreenModel<V, R>;
  readonly flow: FlowModel<V>;
  readonly title: string;
  readonly description?: ReactNode;
  readonly back?: { readonly label: string; readonly onClick: () => void };
  /** "Cancelar" do primeiro passo (o legado volta ao hub de Contratar); o guarda pergunta se há alteração. */
  readonly onCancel: () => void;
  readonly confirm: FlowConfirm;
  /**
   * Desliga o botão principal do passo à vista (o legado desliga o "Continuar" sem produtos,
   * SimulateContainer.tsx:656-664); o Enter também não avança. Um texto desliga e diz por quê, ao lado do botão.
   */
  readonly nextDisabled?: boolean | string | undefined;
  /**
   * Etapas anteriores ao fluxo, concluídas pelo caminho (produto e modalidade do Contratar, W02). Liga o Fluxo
   * guiado (U53): trilho vertical, título "Passo n de N" contando com elas.
   */
  readonly leading?: readonly FlowLeadingStep[] | undefined;
  /**
   * Painel lateral do Fluxo guiado (U53): `true` é o "Suas escolhas" (`FlowSummary`, ≥ 1280 px fixo; 768–1280 px
   * a faixa de chips; iPhone o chip que abre o sheet `?sheet=escolhas`); um nó troca o conteúdo do painel.
   */
  readonly aside?: true | ReactNode;
  /** Aviso no painel "Suas escolhas". */
  readonly summaryNote?: ReactNode;
  /** O conteúdo do passo: `(step) => …` pelo `step.id`. */
  readonly children: (step: FlowStep<V>) => ReactNode;
  readonly className?: string;
}

function useFocusInvalid(epochs: readonly number[], container: React.RefObject<HTMLElement | null>) {
  const key = epochs.join(':');
  const focused = useRef(epochs.map(() => '0').join(':'));
  // Sem dependências: o erro do RHF pode chegar um render depois da tentativa (mesmo motivo do FormScreen).
  useEffect(() => {
    if (focused.current === key) return;
    const first = container.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (!first) return;
    first.focus();
    focused.current = key;
  });
}

/**
 * Arquétipo Fluxo (U39 §1): passos com progresso → revisão → confirmação. Web com a barra de passos e
 * o conteúdo num cartão; iPhone com cabeçalho compacto ("Passo 2 de 4"), voltar no cabeçalho e o botão
 * principal preso embaixo (U12). A troca de passo usa o par `drill` (U38) e leva o foco ao título.
 */
export function FlowScreen<V extends FieldValues, R>({
  screen,
  flow,
  title,
  description,
  back,
  onCancel,
  confirm,
  nextDisabled: nextBlock = false,
  leading = [],
  aside,
  summaryNote,
  children,
  className,
}: FlowScreenProps<V, R>) {
  const { i18n, t } = useLingui();
  const isMobile = useIsMobile();
  const headingId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [confirming, setConfirming] = useState(false);
  const { form, readOnly, isSubmitting, apiError, unmatchedErrors, errorEpoch } = screen;
  const nextDisabled = nextBlock !== false;
  const nextReason = typeof nextBlock === 'string' && nextBlock !== '' ? nextBlock : undefined;
  const nextReasonId = useId();
  const guided = aside !== undefined || leading.length > 0;
  const trail = useFlowTrail(flow, leading);
  const offset = leading.length;
  const { errors } = useFormState({ control: form.control });
  // Erro de campo sem passo à vista (passo escondido por `when`, ou campo de nenhum passo): vai no aviso,
  // da revisão inválida ou da recusa da API (`errorEpoch` ainda não dispensado) até a próxima ação.
  const [orphanCheck, setOrphanCheck] = useState(false);
  const [dismissedEpoch, setDismissedEpoch] = useState(errorEpoch);

  // O passo à vista segue o da URL por uma transição (o antigo precisa ficar no DOM até a foto).
  const [shown, setShown] = useState(flow.current.id);
  const moved = useRef(false);
  const currentId = flow.current.id;
  const direction = flow.direction;
  useEffect(() => {
    if (shown === currentId) return;
    withFlowTransition(direction, () => setShown(currentId));
    moved.current = true;
  }, [currentId, direction, shown]);
  useEffect(() => {
    if (!moved.current) return;
    headingRef.current?.focus({ preventScroll: false });
  }, [shown]);

  // Recusa da API no salvar: volta ao passo do primeiro campo marcado.
  const handled = useRef(0);
  useEffect(() => {
    if (errorEpoch === 0 || handled.current === errorEpoch) return;
    handled.current = errorEpoch;
    const owner = flow.stepWithError();
    if (owner && owner.id !== flow.current.id) flow.goTo(owner.id);
  });
  useFocusInvalid([flow.errorEpoch, errorEpoch], formRef);

  const step = flow.steps.find((item) => item.id === shown) ?? flow.current;
  const stepTitle = resolvePatternText(i18n, step.title);
  const stepDescription = step.description === undefined ? undefined : resolvePatternText(i18n, step.description);
  const current = flow.current;
  const backLabel = current.backLabel === undefined ? t`Voltar` : resolvePatternText(i18n, current.backLabel);
  // Somente leitura só anda entre os passos: sem o verbo do passo ("Simular"), que não roda.
  const primaryLabel = readOnly ? t`Continuar` : current.nextLabel === undefined ? (flow.isReview ? t`Concluir` : t`Continuar`) : resolvePatternText(i18n, current.nextLabel);
  const busy = flow.pending || isSubmitting;

  // Com alteração, quem limpa o rascunho é o "Sim, descartar" do guarda; sem, a saída é livre.
  const cancel = () => {
    if (!screen.isDirty) flow.clearDraft();
    onCancel();
  };

  const primary = async () => {
    if (busy || nextDisabled) return;
    setOrphanCheck(false);
    setDismissedEpoch(errorEpoch);
    if (!flow.isReview) {
      await flow.next();
      return;
    }
    if (readOnly) return;
    const valid = await form.trigger(undefined, { shouldFocus: false });
    if (!valid) {
      const owner = flow.stepWithError();
      if (owner && owner.id !== current.id) flow.goTo(owner.id);
      setOrphanCheck(true);
      return;
    }
    setConfirming(true);
  };

  // W07: a escolha explícita num passo `autoAdvance` avança depois do ✓; trocar de passo antes cancela.
  const primaryRef = useRef(primary);
  useLayoutEffect(() => {
    primaryRef.current = primary;
  });
  const autoAdvance = useAutoAdvance();
  const { cancel: cancelAdvance } = autoAdvance;
  useEffect(() => cancelAdvance, [currentId, cancelAdvance]);
  const onCommit = current.autoAdvance && !readOnly && !nextDisabled ? () => autoAdvance.schedule(() => void primaryRef.current()) : undefined;

  const submitConfirmed = async () => {
    await screen.submit();
    setConfirming(false);
  };

  const orphans = orphanCheck || errorEpoch !== dismissedEpoch ? orphanFieldErrors(errors, flow.steps) : [];
  const orphanAlert =
    orphans.length > 0 ? (
      <Alert tone="err" title={t`Há dados a corrigir`}>
        <p>{t`Estes dados não estão em nenhum passo à vista. Corrija o que levou a eles ou refaça o passo anterior.`}</p>
        <ul className="mt-1 list-disc pl-5">
          {orphans.map((orphan) => (
            <li key={orphan.name}>{orphan.message}</li>
          ))}
        </ul>
      </Alert>
    ) : null;

  const stepAlert = flow.error ? (
    <Alert tone="err" title={t`Não foi possível continuar`}>
      <p>{flow.error.error.feedback().description}</p>
      {flow.error.unmatched.length > 0 ? (
        <ul className="mt-1 list-disc pl-5">
          {flow.error.unmatched.map((error) => (
            <li key={`${error.key}:${error.helpMessage}`}>{error.helpMessage}</li>
          ))}
        </ul>
      ) : null}
    </Alert>
  ) : apiError && !apiError.isCanceled ? (
    <Alert tone="err" title={t`Não foi possível concluir`}>
      <p>{apiError.feedback().description}</p>
      {unmatchedErrors.length > 0 ? (
        <ul className="mt-1 list-disc pl-5">
          {unmatchedErrors.map((error) => (
            <li key={`${error.key}:${error.helpMessage}`}>{error.helpMessage}</li>
          ))}
        </ul>
      ) : null}
    </Alert>
  ) : null;

  const stepIndex = Math.max(0, flow.steps.findIndex((item) => item.id === step.id));
  const stepBody = (
    <div className="flex flex-col gap-5" style={{ viewTransitionName: FLOW_STEP_TRANSITION }} data-step={step.id}>
      {isMobile ? null : guided ? (
        <FlowStepHead eyebrow={t`Etapa ${stepIndex + offset + 1} de ${flow.total + offset}`} title={stepTitle} description={stepDescription} headingId={headingId} headingRef={headingRef} />
      ) : (
        <div className="flex flex-col gap-1">
          <h2 id={headingId} ref={headingRef} tabIndex={-1} className="text-base font-semibold text-foreground outline-none">
            {stepTitle}
          </h2>
          {stepDescription ? <p className="text-sm text-muted-foreground">{stepDescription}</p> : null}
        </div>
      )}
      {isMobile && stepDescription ? <p className="text-sm text-muted-foreground">{stepDescription}</p> : null}
      <ChoiceCommitProvider onCommit={step.id === current.id ? onCommit : undefined}>{children(step)}</ChoiceCommitProvider>
    </div>
  );

  const readOnlyAlert = readOnly ? (
    <Alert tone="info" title={t`Somente leitura`}>
      {t`Você não tem permissão para concluir este fluxo. Peça a quem administra os acessos do seu correspondente.`}
    </Alert>
  ) : null;

  const mobileHeader = (
    <FlowMobileHeader
      title={title}
      position={flow.index + offset + 1}
      total={flow.total + offset}
      stepTitle={resolvePatternText(i18n, current.title)}
      headingId={headingId}
      headingRef={headingRef}
      nav={{ kind: flow.isFirst ? 'cancel' : 'back', label: flow.isFirst ? t`Cancelar` : backLabel, onClick: flow.isFirst ? cancel : () => flow.back(), disabled: busy }}
    />
  );
  const reasonNode = nextReason ? (
    <span id={nextReasonId} className="text-sm text-muted-foreground">
      {nextReason}
    </span>
  ) : null;
  const primaryButton = (size: 'lg' | 'default') => (
    <Button type="submit" size={size} block={size === 'lg'} loading={busy} disabled={nextDisabled} {...(nextReason ? { 'aria-describedby': nextReasonId } : {})}>
      {flow.isReview ? <Check aria-hidden /> : <ArrowRight aria-hidden />}
      {primaryLabel}
    </Button>
  );
  const backButton = flow.isFirst ? (
    <Button type="button" variant="secondary" onClick={cancel} disabled={busy}>
      <X aria-hidden />
      {t`Cancelar`}
    </Button>
  ) : (
    <Button type="button" variant="secondary" onClick={() => flow.back()} disabled={busy}>
      <ArrowLeft aria-hidden />
      {backLabel}
    </Button>
  );
  const showPrimary = !(readOnly && flow.isReview);
  const bar = isMobile ? (
    showPrimary ? (
      <div className="sticky bottom-0 z-20 -mx-4 flex flex-col gap-2 border-t border-border bg-background/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur">
        {reasonNode}
        {primaryButton('lg')}
      </div>
    ) : null
  ) : guided ? (
    // Fluxo guiado (U53): Voltar à esquerda, o principal à direita e o motivo do desligado à vista.
    <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 border-t border-border bg-background/95 py-3 backdrop-blur">
      {backButton}
      <span className="flex-1" />
      {reasonNode}
      {showPrimary ? primaryButton('default') : null}
    </div>
  ) : (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {reasonNode}
      {backButton}
      {showPrimary ? primaryButton('default') : null}
    </div>
  );
  const body = (
    <>
      {readOnlyAlert}
      {stepAlert}
      {orphanAlert}
      <FormProvider {...form}>
        <FormStateProvider readOnly={readOnly}>
          <form
            ref={formRef}
            noValidate
            aria-labelledby={headingId}
            onSubmit={(event) => {
              event.preventDefault();
              void primary();
            }}
            className="flex flex-col gap-5"
          >
            {isMobile || guided ? stepBody : <section className="rounded-card border border-border bg-card p-5">{stepBody}</section>}
            {bar}
          </form>
        </FormStateProvider>
      </FormProvider>
    </>
  );

  return (
    <div data-slot="flow-screen" data-step={flow.current.id} className={cn('flex min-w-0 flex-col gap-5', className)}>
      {guided ? (
        <FlowFrame title={title} description={description} back={back} trail={trail} summary={aside === true} aside={aside === true ? undefined : aside} summaryNote={summaryNote} mobileHeader={mobileHeader}>
          {body}
        </FlowFrame>
      ) : (
        <>
          {isMobile ? (
            mobileHeader
          ) : (
            <>
              <PageHeader title={title} description={description} {...(back ? { back } : {})} />
              <FlowProgress flow={flow} />
            </>
          )}
          {body}
        </>
      )}
      <AlertDialog open={confirming} onOpenChange={(open) => (isSubmitting ? undefined : setConfirming(open))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm.description}</AlertDialogDescription>
          </AlertDialogHeader>
          {confirm.content}
          <AlertDialogFooter className={cn(confirm.editLabel && 'sm:justify-between')}>
            {confirm.editLabel && confirm.editStep ? (
              <Button
                type="button"
                variant="ghost"
                disabled={isSubmitting}
                onClick={() => {
                  setConfirming(false);
                  if (confirm.editStep) flow.goTo(confirm.editStep);
                }}
              >
                {confirm.editLabel}
              </Button>
            ) : null}
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <AlertDialogCancel disabled={isSubmitting}>{confirm.cancelLabel ?? t`Cancelar`}</AlertDialogCancel>
              <Button type="button" loading={isSubmitting} onClick={() => void submitConfirmed()}>
                {confirm.submitLabel}
              </Button>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={screen.guard.blocked} onOpenChange={(open) => (open ? undefined : screen.guard.stay())}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Descartar as alterações?`}</AlertDialogTitle>
            <AlertDialogDescription>{t`O que você preencheu neste fluxo ainda não foi enviado e vai se perder.`}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={screen.guard.stay}>{t`Continuar preenchendo`}</AlertDialogCancel>
            <AlertDialogAction
              tone="danger"
              onClick={() => {
                flow.clearDraft();
                screen.guard.proceed();
              }}
            >
              {t`Sim, descartar`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
